<template>
  <div class="w-full flex flex-col" style="max-width: 1104px; container-type: inline-size;">
    <div class="w-full grid grid-cols-2">
      <!-- Header and Actions -->
      <div class="w-full flex flex-col p-7">
        <div class="flex items-center gap-1">
          <AgencyDirectorySettingsIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-normal">
            Agency Directory
          </BaseText>
        </div>
        <BaseText class="heading-text text-text-loud my-3" style="max-width: 420px;">
          Expand your reach and get inbound leads for your agency.
        </BaseText>
        <BaseText type="body" size="md" class="text-text-loud" style="max-width: 440px;">
          Set up your agency profile and get matched with business owners and executives looking to hire expert help for their marketing projects.
        </BaseText>
        <div v-if="userHasAccess" class="flex items-center gap-2.5 mt-14">
          <button class="submit-btn px-3 py-1.5 rounded-md" @click="$emit('createDirectoryListing')">
            <BaseText type="label" size="sm" class="text-white">
              List My Agency
            </BaseText>
          </button>
          <a href="https://www.foreplay.co/agency-directory" target="_blank">
            <button class="secondary-btn px-3 py-1.5 rounded-md">
              <BaseText type="label" size="sm" class="text-text-muted">
                View Directory
              </BaseText>
            </button>
          </a>
        </div>
        <div v-else class="flex items-center gap-1.5 p-1.5 rounded-md bg-neutral-25 mt-14 w-max">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M13 8.5V6.25C13 4.59314 11.6568 3.25 10 3.25C8.34314 3.25 7 4.59314 7 6.25V8.5M10 11.5V13.75M6.25 16.75H13.75C14.5784 16.75 15.25 16.0784 15.25 15.25V10C15.25 9.17155 14.5784 8.5 13.75 8.5H6.25C5.42157 8.5 4.75 9.17155 4.75 10V15.25C4.75 16.0784 5.42157 16.75 6.25 16.75Z" 
            stroke="#303546" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ accessBlockedText }}
          </BaseText>
        </div>
      </div>
      <AgencyCreatePreview />
    </div>
    <!-- Benefits List -->
    <div class="benefits-container w-full p-7 mt-4">
      <div v-for="(benefit, index) in benefitsList" :key="`benefit-${index}`" class="flex flex-col" style="width: 320px;">
        <!-- Asset -->
        <div class="w-full">
          <component v-if="benefit.asset" :is="benefit.asset" />
        </div>
        <!-- Title -->
        <BaseText type="label" size="sm" class="text-text-muted mt-5">
          {{ benefit.title }}
        </BaseText>
        <!-- Description -->
        <BaseText type="body" size="sm" class="text-text-normal mt-1">
          {{ benefit.description }}
        </BaseText>
      </div>
    </div>
    <!-- FAQ Section -->
    <div class="w-full flex justify-center mt-9 px-7">
      <div class="w-full flex gap-6" style="max-width: 900px;">
        <div class="flex flex-col flex-shrink-0" style="width: 280px;">
          <BaseText type="heading" size="sm" class="text-text-muted">
            FAQ
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-1">
            Questions about the agency directory.
          </BaseText>
          <button class="secondary-btn flex items-center gap-1.5 px-2.5 py-2 rounded-md w-max mt-5" @click="showIntercom">
            <ContactSupportIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Contact Support
            </BaseText>
          </button>
        </div>
        <!-- QA List -->
        <div class="flex-grow flex flex-col min-w-0">
          <div v-for="(faqItem, index) in faqItems" :key="`faq-item-${index}`"
            class="w-full flex flex-col border-b border-border-normal">
            <button class="w-full flex items-center justify-between py-5" @click="toggleFaqAnswer(index)">
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ faqItem.question }}
              </BaseText>
              <div class="transform transition-transform"
                :style="{ transform: faqItem.expanded ? 'scaleY(-1)' : 'scaleY(1)' }">
                <ChevronIcon class="text-icon-muted" />
              </div>
            </button>
            <div ref="faqAnswers" class="relative overflow-hidden">
              <transition name="answer">
                <BaseText v-if="faqItem.expanded" type="body" size="sm"
                  class="text-text-normal pb-5 pr-20 whitespace-pre-line">
                  {{ faqItem.answer }}
                </BaseText>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import smoothReflow from 'vue-smooth-reflow'

// UI Previews / Icons
import AgencyCreatePreview from './ui-previews/AgencyCreatePreview.vue'
import VerifiedBrandedProfilePreview from './ui-previews/VerifiedBrandedProfilePreview.vue'
import AgencyBadgeCardPreview from './ui-previews/AgencyBadgeCardPreview.vue'
import NewsletterFeaturesPreview from './ui-previews/NewsletterFeaturesPreview.vue'
import AgencyDirectorySettingsIcon from '../../globals/Icons/SettingsIcons/AgencyDirectorySettingsIcon.vue'
import ContactSupportIcon from '../../globals/Icons/ContactSupportIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'

const benefitsList = [
  {
    title: 'Verified Branded Profile',
    description: 'Set up your page in under 15 minutes to drive website traffic from over 500k monthly visitors.',
    asset: VerifiedBrandedProfilePreview
  },
  {
    title: 'Agency Badge',
    description: 'Get your “Foreplay Agency” badge to embed on your website.',
    asset: AgencyBadgeCardPreview
  },
  {
    title: 'Newsletter Features',
    description: 'Submit case studies and agency wins to be featured in the Foreplay newsletter (300k subs)',
    asset: NewsletterFeaturesPreview
  }
]

const faqContent = [
  {
    question: 'Is searching for an agency free?',
    answer: 'Yes, feel free to browse the agency directory here. When you find one you like submit the contact form and we will connect you.'
  },
  {
    question: 'How can I list my agency?',
    answer: 'Access to the agency directory is included on the Agency Plan. If you’re on another plan you can reach out directly to see if you qualify.'
  },
  {
    question: 'What are "Verified Agencies"?',
    answer: 'Verified agencies are vetted by our team to ensure they meet our quality standards. Often they have been given a referral by Foreplay already and successfully serviced the referral.'
  },
  {
    question: 'What are the benefits of being listed?',
    answer: 'Foreplay is used by over 20,000 marketers from DTC brands to B2B GTM teams. Being listed in the directory gives you exposure to these marketers along with being featured in our newsletter of over 300,000 marketers.'
  }
]

export default {
  name: 'CreateDirectoryListing',
  mixins: [smoothReflow],
  components: {
    AgencyCreatePreview,
    AgencyDirectorySettingsIcon,
    ContactSupportIcon,
    ChevronIcon
  },
  data () {
    return {
      benefitsList,
      faqItems: faqContent.map(item => ({
        ...item,
        expanded: true
      }))
    }
  },
  mounted () {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.applyReflowToAnswers()
      })
    })
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserSubscription', 'isFreeTier', 'getTeam']),
    userHasAccess () {
      return !this.isFreeTier && this.getUserSubscription?.internalProductId === 'agency_plan' && this.getTeam
    },
    accessBlockedText () {
      if (this.userHasAccess) return ''
      if (this.getUserSubscription?.internalProductId !== 'agency_plan')
        return 'You need to upgrade your plan to Agency.'
      if (!this.getTeam)
        return 'You need to create a team to list your agency.'
      return 'Not available during trial period.'
    }
  },
  methods: {
    showIntercom () {
      window.Intercom('show')
    },
    toggleFaqAnswer (index) {
      this.faqItems = this.faqItems.map((item, i) => ({
        ...item,
        expanded: i === index ? !item.expanded : item.expanded
      }))
    },
    applyReflowToAnswers () {
      const answerRefs = this.$refs.faqAnswers
      if (!answerRefs) {
        console.error('No answer refs found')
        return
      }
      answerRefs.forEach(answer => {
        this.$smoothReflow({
          el: answer,
          property: 'height',
          transition: 'height 250ms ease-in-out'
        })
      })
    }
  }
}
</script>

<style scoped>
.heading-text {
  font-size: 26px !important;
  font-style: normal;
  font-weight: 600;
  line-height: 36px; /* 128.571% */
}

.benefits-container {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}
@container (width < 1050px) {
  .benefits-container {
    justify-content: start;
    gap: 24px;
  }
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}

.answer-enter-active {
  transition: opacity 250ms ease-in-out;
}

.answer-leave-active {
  transition: opacity 250ms ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.answer-enter-from,
.answer-enter,
.answer-leave-to {
  opacity: 0;
}

.answer-enter-to,
.answer-leave-from {
  opacity: 1;
}
</style>